import { Alert, Box, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { ComposedAvatar } from '../../shared/compose-avatar'
import usePrefersReducedMotion from './usePrefersReducedMotion'

export interface PreviewCanvasProps {
  composed: ComposedAvatar
  /** The section being varied — only ITS layers animate when they change. */
  selectedSectionId?: string
  size?: number
  alt: string
}

export default function PreviewCanvas({
  composed,
  selectedSectionId,
  size = 320,
  alt,
}: PreviewCanvasProps) {
  const { formatMessage } = useIntl()
  const reducedMotion = usePrefersReducedMotion()

  return (
    <Stack spacing={1} alignItems="center">
      <Box
        role="img"
        aria-label={alt}
        data-testid="avatar-preview-canvas"
        sx={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'action.hover',
          '@keyframes avatarPreviewFade': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        {composed.layers.map((layer) =>
          layer.kind === 'fill' ? (
            <Box
              key={layer.key}
              data-testid={`preview-layer-${layer.key}`}
              data-section={layer.sectionId}
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: layer.colour,
                opacity: layer.opacity,
              }}
            />
          ) : (
            // A plain img, not Box: MUI consumes width/height as style props and never emits
            // the attributes that reserve the space before a layer loads.
            <img
              key={layer.key}
              src={layer.url}
              alt=""
              aria-hidden={true}
              width={size}
              height={size}
              decoding="async"
              data-testid={`preview-layer-${layer.key}`}
              data-section={layer.sectionId}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: layer.fit === 'cover' ? 'cover' : 'contain',
                mixBlendMode: layer.blend,
                opacity: layer.opacity,
                // Only the varied section animates: everything else holding still is what
                // makes the change legible. Nothing animates under reduced motion.
                animation:
                  !reducedMotion && layer.sectionId === selectedSectionId
                    ? 'avatarPreviewFade 120ms ease-in'
                    : 'none',
              }}
            />
          ),
        )}
      </Box>

      {composed.missing.length > 0 && (
        <Alert severity="info" sx={{ maxWidth: size * 1.4 }}>
          {formatMessage(
            { id: 'avatarStudio.preview.missingSections' },
            { names: composed.missing.join(', ') },
          )}
        </Alert>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ maxWidth: size * 1.4, textAlign: 'center' }}
      >
        {formatMessage({ id: 'avatarStudio.preview.approximate' })}
      </Typography>
    </Stack>
  )
}
